import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../supabase';
import styles from './ResearcherRanking.module.css';
import TabMenu from '../components/TabMenu';
import CountUp from 'react-countup';

function ResearcherRanking() {
  const [rankings, setRankings] = useState([]);
  const [session, setSession] = useState(null);
  const [prevValues, setPrevValues] = useState({});

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
    fetchRankings();

    // Realtime subscription 설정
    const channel = supabase.channel('public:ideas_and_investments')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ideas',
          filter: 'status=eq.in_progress'
        },
        () => {
          updateRankingsWithPrevValues();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'idea_investments'
        },
        () => {
          updateRankingsWithPrevValues();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles'
        },
        () => {
          updateRankingsWithPrevValues();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const updateRankingsWithPrevValues = async () => {
    // 현재 값을 이전 값으로 저장
    const prevValueMap = {};
    rankings.forEach(user => {
      prevValueMap[user.user_id] = user.totalValue;
    });
    setPrevValues(prevValueMap);

    // 새로운 랭킹 정보 가져오기
    await fetchRankings();
  };

  const fetchRankings = async () => {
    try {
      // 1. 모든 유저의 프로필 정보 가져오기 (user_type_id != 2 필터 추가)
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('*')
        .neq('user_type_id', 2);  // user_type_id가 2가 아닌 계정만 선택

      if (profilesError) throw profilesError;

      // 2. 진행 중인 아이디어 정보 가져오기
      const { data: ideas, error: ideasError } = await supabase
        .from('ideas')
        .select('*')
        .eq('status', 'in_progress');

      if (ideasError) throw ideasError;

      // 3. 진행 중인 아이디어에 대한 투자 정보 가져오기
      const { data: investments, error: investmentsError } = await supabase
        .from('idea_investments')
        .select(`
          *,
          ideas!inner (
            status
          )
        `)
        .eq('ideas.status', 'in_progress');

      if (investmentsError) throw investmentsError;

      // 4. 각 유저별 total value 계산
      const userValues = profiles.map(profile => {
        // 4.1. 본인이 만든 아이디어의 total_investment 합계
        const ownIdeasValue = ideas
          .filter(idea => idea.user_id === profile.user_id)
          .reduce((sum, idea) => sum + (idea.total_investment || 0), 0);

        // 4.2. 투자한 아이디어의 현재 가치 합계
        const investmentsValue = investments
          .filter(inv => inv.user_id === profile.user_id)
          .reduce((sum, inv) => sum + (inv.value || 0), 0);

        // 4.3. 현재 보유 예산
        const currentBudget = profile.current_budget || 0;

        return {
          user_id: profile.user_id,
          name: profile.name || '익명',
          totalValue: ownIdeasValue + investmentsValue + currentBudget
        };
      });

      // 5. total value 기준으로 정렬
      const sortedRankings = userValues
        .sort((a, b) => b.totalValue - a.totalValue)
        .filter(user => user.totalValue > 0);

      setRankings(sortedRankings);
    } catch (error) {
      console.error('Error fetching rankings:', error);
    }
  };

  const getRankDisplay = (index) => {
    switch (index) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return index + 1;
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>개인 순위</h1>
      </header>

      <main className={styles.main}>
        <div className={styles.rankingList}>
          {rankings.map((user, index) => (
            <div key={user.user_id} className={styles.rankingItem}>
              <div className={styles.rankInfo}>
                <span className={`${styles.rank} ${index < 3 ? styles.medalRank : ''}`}>
                  {getRankDisplay(index)}
                </span>
                <div className={styles.userInfo}>
                  <span className={styles.userName}>{user.name}</span>
                  {session?.user?.id === user.user_id && (
                    <span className={styles.myLabel}>나</span>
                  )}
                </div>
              </div>
              <p className={styles.totalValue}>
                <CountUp
                  start={prevValues[user.user_id] || 0}
                  end={user.totalValue}
                  duration={2.5}
                  separator=","
                  suffix="원"
                  useEasing={true}
                />
              </p>
            </div>
          ))}
        </div>
      </main>

      <TabMenu />
    </div>
  );
}

export default ResearcherRanking; 